import * as React from 'react';
import axios from 'axios';
import ACTIONS from '../actions';
import { API_BASE_URL } from '../../config';
import useNotification from '../notification/notificationContext';
import { NOTIFICATIONS } from '../../config';


export const ClinicsContext = React.createContext();

function useClinics() {
  const context = React.useContext(ClinicsContext);
  if (!context) throw new Error('useClinics must be used within a ClinicsProvider');

  const [clinicsState, dispatch] = context;
  
  const { setNotification } = useNotification();

  const token = localStorage.getItem('jwtToken');
  if (token) axios.defaults.headers.common['x-access-token'] = token;

  //get all clinics
  const getAllClinics = () => {
    return axios.get(`${API_BASE_URL}/clinic/get-all`).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.GET_ALL_CLINICS,
        payload: response.data
      })
      return response.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of clinics from the server. Please try again later.'
      })
      throw err;
    })
  }

  const getPartnerClinics = (partnerId) => {
    return axios.get(`${API_BASE_URL}/clinic/get-partner-clinics/${partnerId}`).then(res => {
      const response = res.data;
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of clinics from the server. Please try again later.'
      })
      throw err;
    })
  }

  const getClinicDetails = (id) => {
    return axios.get(`${API_BASE_URL}/clinic/get-details/${id}`).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to get clinic details. Please try again later.'
      })
      throw err;
    })
  }

  //create clinic
  const createClinic = (data) => {
    return axios.post(`${API_BASE_URL}/clinic/create`, data).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.CREATE_CLINIC,
        payload: response.data
      })
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Clinic added successfully!'
      })
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data.message || 'We were unable to create the clinic. Please try again later.'
      })
      throw err;
    })
  }

  //edit clinic
  const editClinic = (id, data) => {
    return axios.patch(`${API_BASE_URL}/clinic/edit/${id}`, data).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.EDIT_CLINIC,
        payload: response.data
      })
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Clinic updated successfully.'
      })
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data.message || 'We were unable to edit the clinic. Please try again later.'
      })
      throw err;
    })
  }

  //delete clinic
  const deleteClinic = (id) => {
    return axios.delete(`${API_BASE_URL}/clinic/delete/${id}`).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.DELETE_CLINIC,
        payload: response.data
      }) 
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Request completed',
        message: 'Clinic was sucessfully deleted.'
      })
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data.message || 'We were unable to delete the clinic(s). Please try again later.'
      })
      throw err;
    })
  }

  //delete clinic
  const addClinicPartner = (clinicId, partnerId) => {
    return axios.post(`${API_BASE_URL}/clinic/add-partner-to-clinic/${clinicId}/${partnerId}`).then(res => {
      const response = res.data;
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Request completed',
        message: 'Partner successfully added.'
      })
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data.message || 'We were unable to add partner. Please try again later.'
      })
      throw err;
    })
  }

  //delete clinic
  const removeClinicPartner = (clinicId, partnerId) => {
    return axios.post(`${API_BASE_URL}/clinic/remove-partner-from-clinic/${clinicId}/${partnerId}`).then(res => {
      const response = res.data;
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Request completed',
        message: 'Partner successfully removed.'
      })
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data.message || 'We were unable to remove partner. Please try again later.'
      })
      throw err;
    })
  }

  return {
    clinicsState,
    getAllClinics,
    createClinic,
    editClinic,
    deleteClinic,
    getClinicDetails,
    addClinicPartner,
    removeClinicPartner,
    getPartnerClinics
  }
}

export default useClinics